import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { FolderSchema } from '../api/contracts'

/**
 * Per-spec: expand/collapse state is persisted LOCALLY (not to the backend).
 * Organize-mode edits live in this store while the user drags/creates/renames;
 * on Save the flat folder list is sent to the backend.
 */

interface PersistedState {
  /** Folder IDs that are currently expanded in the sidebar tree. */
  expandedFolderIds: string[]
}

interface TableOrganizationState extends PersistedState {
  /** Working copy of folders while in organize mode (null = not editing). */
  draftFolders: FolderSchema[] | null
  /** True while user is in organize mode. */
  isOrganizing: boolean
  /** True if draftFolders differ from last-saved state. */
  isDirty: boolean

  // Expand/collapse (used in both read and organize modes)
  toggleFolder: (folderId: string) => void
  expandFolders: (folderIds: string[]) => void
  collapseAll: () => void
  isExpanded: (folderId: string) => boolean

  // Organize mode lifecycle
  enterOrganizeMode: (currentFolders: FolderSchema[]) => void
  exitOrganizeMode: () => void

  // Draft mutations (only valid while isOrganizing)
  setDraft: (folders: FolderSchema[]) => void
  addFolder: (name: string, parentId: string | null) => string
  renameFolder: (folderId: string, name: string) => void
  deleteFolder: (folderId: string) => void
}

const STORAGE_KEY = 'erp-table-organization-ui'

export const useTableOrganizationStore = create<TableOrganizationState>()(
  persist(
    (set, get) => ({
      expandedFolderIds: [],
      draftFolders: null,
      isOrganizing: false,
      isDirty: false,

      toggleFolder: (folderId) =>
        set((state) => {
          const s = new Set(state.expandedFolderIds)
          if (s.has(folderId)) s.delete(folderId)
          else s.add(folderId)
          return { expandedFolderIds: Array.from(s) }
        }),

      expandFolders: (folderIds) =>
        set((state) => {
          const s = new Set(state.expandedFolderIds)
          for (const id of folderIds) s.add(id)
          return { expandedFolderIds: Array.from(s) }
        }),

      collapseAll: () => set({ expandedFolderIds: [] }),

      isExpanded: (folderId) => get().expandedFolderIds.includes(folderId),

      enterOrganizeMode: (currentFolders) =>
        set({
          isOrganizing: true,
          // Deep clone so edits don't mutate server state
          draftFolders: currentFolders.map((f) => ({
            ...f,
            tables: f.tables.map((t) => ({ ...t })),
          })),
          isDirty: false,
        }),

      exitOrganizeMode: () =>
        set({
          isOrganizing: false,
          draftFolders: null,
          isDirty: false,
        }),

      setDraft: (folders) => set({ draftFolders: folders, isDirty: true }),

      addFolder: (name, parentId) => {
        const id = crypto.randomUUID()
        set((state) => {
          if (!state.draftFolders) return state
          // Append at the end of siblings at this level
          const siblings = state.draftFolders.filter((f) => f.parent_id === parentId)
          const nextSortOrder = siblings.length
          const newFolder: FolderSchema = {
            id,
            name,
            parent_id: parentId,
            sort_order: nextSortOrder,
            tables: [],
          }
          return {
            draftFolders: [...state.draftFolders, newFolder],
            isDirty: true,
          }
        })
        return id
      },

      renameFolder: (folderId, name) =>
        set((state) => {
          if (!state.draftFolders) return state
          return {
            draftFolders: state.draftFolders.map((f) => (f.id === folderId ? { ...f, name } : f)),
            isDirty: true,
          }
        }),

      deleteFolder: (folderId) =>
        set((state) => {
          if (!state.draftFolders) return state
          // Collect the folder and all its descendants
          const toRemove = new Set<string>([folderId])
          let changed = true
          while (changed) {
            changed = false
            for (const f of state.draftFolders) {
              if (f.parent_id && toRemove.has(f.parent_id) && !toRemove.has(f.id)) {
                toRemove.add(f.id)
                changed = true
              }
            }
          }
          return {
            draftFolders: state.draftFolders.filter((f) => !toRemove.has(f.id)),
            isDirty: true,
          }
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist expand/collapse — drafts & organize mode are session-local
      partialize: (state): PersistedState => ({
        expandedFolderIds: state.expandedFolderIds,
      }),
    }
  )
)
